/**
 * Error Handling Utilities
 * Centralized error handling patterns for consistent logging and error extraction
 *
 * Note: These utilities focus on error processing and logging.
 * For user-facing toast notifications, import toast directly from '@kit/ui/sonner'
 * in your components.
 */
import { getLogger } from '@kit/logger';

/**
 * Handle action errors with consistent logging
 * @param error - The error that occurred
 * @param fallbackMessage - Fallback message if error doesn't have a message
 * @param context - Optional context for debugging
 */
export async function handleActionError(
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>,
): Promise<void> {
  const message = error instanceof Error ? error.message : fallbackMessage;

  // Log in development mode with structured logging
  if (process.env.NODE_ENV === 'development') {
    const logger = await getLogger();
    logger.error(
      {
        service: 'error-handler',
        message,
        error: error instanceof Error ? error.message : String(error),
        context,
        timestamp: new Date().toISOString(),
      },
      'Action error',
    );
  }
}

/**
 * Handle successful actions with logging
 * @param message - Success message to log
 */
export async function handleActionSuccess(message: string): Promise<void> {
  if (process.env.NODE_ENV === 'development') {
    const logger = await getLogger();
    logger.info({ service: 'error-handler' }, message);
  }
}

/**
 * Extract error message from unknown error type
 * @param error - The error to extract message from
 * @param fallback - Fallback message if no message found
 * @returns Error message string
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  return fallback;
}

/**
 * Handle async action with error handling and logging
 * Useful for form submissions and data mutations
 *
 * Note: For user-facing notifications, use toast.promise() from '@kit/ui/sonner' instead:
 * @example
 * ```typescript
 * import { toast } from '@kit/ui/sonner';
 * await toast.promise(myAction(), {
 *   loading: 'Processing...',
 *   success: 'Success!',
 *   error: 'Failed'
 * });
 * ```
 */
export async function handleAsyncAction<T>(
  action: () => Promise<T>,
  options: {
    errorMessage?: string;
    onSuccess?: (result: T) => void;
    onError?: (error: unknown) => void;
  } = {},
): Promise<{ success: boolean; data?: T; error?: unknown }> {
  try {
    const result = await action();
    options.onSuccess?.(result);
    return { success: true, data: result };
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      options.errorMessage || 'An error occurred',
    );

    if (process.env.NODE_ENV === 'development') {
      const logger = await getLogger();
      logger.error(
        {
          service: 'error-handler',
          errorMessage,
          error: error instanceof Error ? error.message : String(error),
        },
        'Async action error',
      );
    }

    options.onError?.(error);
    return { success: false, error };
  }
}
